/** @file Card Acceptance section */

import React, { useState } from 'react';
import { Box, FormControlLabel, Checkbox, Typography } from '@mui/material';
import { testId as parentTestId } from '../util';
import { CardBrand } from '../../../util/constants';

export const testId = `${parentTestId}-CARD_ACCEPTANCE`;

type Props = {
  nonAcceptedCardBrands?: CardBrand[];
  onNonAcceptedCardBrandsChange: (nonAcceptedCardBrands: CardBrand[]) => void;
  disabled?: boolean;
};

const checkboxes: Array<{ label: string; value: CardBrand | 'selectAll' }> = [
  { label: 'Select All', value: 'selectAll' },
  { label: 'American Express', value: 'amex' },
  { label: 'Mastercard', value: 'mastercard' },
  { label: 'Visa', value: 'visa' },
  { label: 'Discover', value: 'discover' },
];

const getCheckboxValues = (nonAcceptedCardBrands: CardBrand[] | undefined) => {
  const checkboxValues: Record<CardBrand, boolean> = {
    amex: false,
    discover: false,
    mastercard: false,
    visa: false,
  };
  if (nonAcceptedCardBrands) {
    Object.keys(checkboxValues).forEach((cardType) => {
      checkboxValues[cardType] = !nonAcceptedCardBrands.includes(cardType as CardBrand);
    });
  }
  return checkboxValues;
};

const CardAcceptance: React.FC<Props> = ({
  nonAcceptedCardBrands,
  onNonAcceptedCardBrandsChange,
  disabled,
}: Props) => {
  const defaultCheckboxValues = getCheckboxValues(nonAcceptedCardBrands);
  const [checkboxValues, setCheckboxValues] = useState(defaultCheckboxValues);
  const [all, setAll] = useState(Object.values(defaultCheckboxValues).every((value) => value));

  const handleCheckboxChange = (checkboxName: string) => {
    let newCheckboxValues: Record<CardBrand, boolean> = { ...checkboxValues };
    if (checkboxName === 'selectAll') {
      setAll((prevSelectAll) => !prevSelectAll);
      newCheckboxValues = {
        amex: !all,
        discover: !all,
        mastercard: !all,
        visa: !all,
      };
    } else {
      newCheckboxValues[checkboxName] = !checkboxValues[checkboxName];
      const allCheckboxesChecked = Object.values(newCheckboxValues).every((value) => value);
      setAll(allCheckboxesChecked);
    }

    onNonAcceptedCardBrandsChange(
      Object.keys(newCheckboxValues).filter((key) => !newCheckboxValues[key]) as CardBrand[],
    );
    setCheckboxValues(newCheckboxValues);
  };

  const renderCheckbox = (label: string, name: string) => (
    <Box key={name}>
      <FormControlLabel
        sx={{
          '& .MuiFormControlLabel-label': { ml: 1 },
          '& .MuiCheckbox-root': {
            py: 0.5,
            pr: 1,
            pl: 0,
          },
          ml: 0,
        }}
        control={
          <Checkbox
            checked={checkboxValues[name] || all}
            onChange={() => handleCheckboxChange(name)}
            disabled={disabled}
          />
        }
        data-testid={`${testId}-${name}`}
        label={label}
        id={name}
      />
    </Box>
  );

  return (
    <Box component="section" data-testid={testId}>
      <Typography variant="large" fontWeight="bold" sx={{ mb: 1 }}>
        Card Acceptance
      </Typography>
      <Typography variant="small" color="text.secondary">
        Please select which networks you accept. Card networks that are not accepted will be hidden
        from customers.
      </Typography>
      <Box sx={{ my: 2 }}>
        <Typography variant="small" fontWeight="semibold">
          Accepted networks
        </Typography>
      </Box>
      {checkboxes.map(({ label, value }) => renderCheckbox(label, value))}
    </Box>
  );
};

export default CardAcceptance;
